import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, ArrowLeft, Tag, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader } from './ui/card';
import Header from './Header';
import Footer from './Footer';
import { articulosBlog as mockArticulos } from '../data/mock';

const BlogArticle = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [articulo, setArticulo] = useState(null);
  const [articulosRelacionados, setArticulosRelacionados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    cargarArticulo();
  }, [slug]);

  const cargarArticulo = async () => {
    setLoading(true);
    setError(false);
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/blog/${slug}`);
      const data = await response.json();

      if (data.success && data.articulo) {
        setArticulo(data.articulo);
        cargarRelacionados(data.articulo.categoria, data.articulo.id);
      } else {
        // Fallback a mock data
        const articuloMock = mockArticulos.find(a => a.slug === slug);
        if (articuloMock) {
          setArticulo(articuloMock);
          const relacionados = mockArticulos
            .filter(a => a.categoria === articuloMock.categoria && a.id !== articuloMock.id)
            .slice(0, 3);
          setArticulosRelacionados(relacionados);
        } else {
          setError(true);
        }
      }
    } catch (err) {
      console.error('Error cargando artículo:', err);
      // Fallback a mock data
      const articuloMock = mockArticulos.find(a => a.slug === slug);
      if (articuloMock) {
        setArticulo(articuloMock);
        const relacionados = mockArticulos
          .filter(a => a.categoria === articuloMock.categoria && a.id !== articuloMock.id)
          .slice(0, 3);
        setArticulosRelacionados(relacionados);
      } else {
        setError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const cargarRelacionados = async (categoria, articuloId) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/blog?publicado=true&categoria=${encodeURIComponent(categoria)}&limit=4`);
      const data = await response.json();

      if (data.success && data.articulos) {
        const relacionados = data.articulos.filter(a => a.id !== articuloId).slice(0, 3);
        setArticulosRelacionados(relacionados);
      }
    } catch (err) {
      console.error('Error cargando artículos relacionados:', err);
    }
  };

  const formatearFecha = (fecha) => {
    const date = new Date(fecha);
    return date.toLocaleDateString('es-ES', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Convertir el contenido con saltos de línea y formato Markdown básico
  const renderContenido = (contenido) => {
    return contenido.split('\n').map((parrafo, index) => {
      if (parrafo.startsWith('## ')) {
        return <h2 key={index} className='heading-2 mt-8 mb-4'>{parrafo.replace('## ', '')}</h2>;
      } else if (parrafo.startsWith('### ')) {
        return <h3 key={index} className='heading-3 mt-6 mb-3'>{parrafo.replace('### ', '')}</h3>;
      } else if (parrafo.startsWith('- ')) {
        return <li key={index} className='body-medium ml-6'>{parrafo.replace('- ', '')}</li>;
      } else if (parrafo.match(/^\d+\. /)) {
        return <li key={index} className='body-medium ml-6'>{parrafo.replace(/^\d+\. /, '')}</li>;
      } else if (parrafo.trim() === '') {
        return <br key={index} />;
      } else {
        return <p key={index} className='body-medium mb-4'>{parrafo}</p>;
      }
    });
  };

  if (loading) {
    return (
      <div className='min-h-screen bg-[var(--bg-page)]'>
        <Header />
        <div className='container mx-auto px-4 py-24 flex justify-center items-center'>
          <Loader2 className="animate-spin text-[var(--brand-dark)]" size={48} />
          <span className="ml-4 body-large">Cargando artículo...</span>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !articulo) {
    return (
      <div className='min-h-screen bg-[var(--bg-page)]'>
        <Header />
        <div className='container mx-auto px-4 py-24'>
          <div className='text-center'>
            <h1 className='heading-1 mb-4'>Artículo no encontrado</h1>
            <p className='body-large text-[var(--text-light)] mb-6'>
              Lo sentimos, el artículo que buscas no existe o ha sido eliminado.
            </p>
            <Button className='btn-primary' onClick={() => navigate('/blog')} data-testid="volver-al-blog">
              Volver al Blog
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-[var(--bg-page)]'>
      <Header />
      
      <article className='container mx-auto px-4 py-24 md:py-32'>
        <div className='max-w-4xl mx-auto'>
          {/* Botón volver */}
          <Button 
            variant='ghost' 
            className='mb-6'
            onClick={() => navigate('/blog')}
            data-testid="btn-volver-blog"
          >
            <ArrowLeft className='mr-2 h-4 w-4' />
            Volver al Blog
          </Button>

          {/* Imagen destacada */}
          <div className='relative aspect-video rounded-3xl overflow-hidden mb-8'>
            <img 
              src={articulo.imagen} 
              alt={articulo.titulo}
              className='w-full h-full object-cover'
            />
          </div>

          {/* Metadata */}
          <div className='flex flex-wrap items-center gap-4 mb-6'>
            <div className='flex items-center gap-2 text-[var(--text-light)]'>
              <Calendar size={18} />
              <span className='body-medium'>{formatearFecha(articulo.fecha || articulo.created_at)}</span>
            </div>
            <div className='flex items-center gap-2'>
              <Tag size={18} className='text-[var(--brand-dark)]' />
              <span className='bg-[var(--brand-primary)] text-[var(--brand-dark)] px-3 py-1 rounded-full text-sm font-semibold'>
                {articulo.categoria}
              </span>
            </div>
          </div>

          {/* Título */}
          <h1 className='display-medium mb-6' data-testid="articulo-titulo">{articulo.titulo}</h1>

          {/* Extracto */}
          <p className='body-large text-[var(--text-secondary)] mb-8 pb-8 border-b'>
            {articulo.extracto}
          </p>

          {/* Contenido */}
          <div className='prose prose-lg max-w-none'>
            {renderContenido(articulo.contenido)}
          </div>

          {/* CTA */}
          <Card className='network-card bg-[var(--brand-dark)] text-white mt-12'>
            <CardContent className='pt-6 text-center'>
              <h3 className='heading-3 text-white mb-4'>¿Necesitas tratamiento personalizado?</h3>
              <p className='body-medium text-white opacity-90 mb-6'>
                Reserva una consulta y te ayudaré con tu problema específico
              </p>
              <Button 
                className='btn-primary'
                onClick={() => navigate('/#reservas')}
                data-testid="btn-reservar-consulta"
              >
                Reservar Consulta
              </Button>
            </CardContent>
          </Card>

          {/* Artículos relacionados */}
          {articulosRelacionados.length > 0 && (
            <div className='mt-16'>
              <h3 className='heading-2 mb-6'>Artículos Relacionados</h3>
              <div className='grid md:grid-cols-3 gap-6'>
                {articulosRelacionados.map((art) => (
                  <Card 
                    key={art.id || art.slug}
                    className='network-card cursor-pointer'
                    onClick={() => navigate(`/blog/${art.slug}`)}
                    data-testid={`relacionado-${art.slug}`}
                  >
                    <CardHeader className='p-0'>
                      <div className='relative aspect-video rounded-t-[32px] overflow-hidden'>
                        <img 
                          src={art.imagen} 
                          alt={art.titulo}
                          className='w-full h-full object-cover'
                        />
                      </div>
                    </CardHeader>
                    <CardContent className='pt-4'>
                      <h4 className='heading-3 mb-2 line-clamp-2'>{art.titulo}</h4>
                      <p className='body-small text-[var(--text-light)] line-clamp-2'>{art.extracto}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
      
      <Footer />
    </div>
  );
};

export default BlogArticle;
